import { confirmDialog } from "../components/confirmHost.jsx";
import { toast } from "../components/Toast.jsx";
import React, { useEffect, useRef, useState } from "react";
import { Upload, FileText, Plus, Trash2, RefreshCw, CheckCircle2, AlertTriangle, X, Edit3, Eye, BookOpen, Calendar, Users } from "lucide-react";
import GradientHeader from "../components/GradientHeader.jsx";
import * as pdfjsLib from "pdfjs-dist";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useApiData, fetchApi } from "../lib/api.js";
import { getCurrentUser } from "../lib/auth.js";
import { MD_COMPONENTS } from "./trener/DokumentDetail.jsx";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

// Vytáhne text z PDF v prohlížeči (obchází 6MB limit Netlify funkce — posílá se jen text).
async function extractPdfText(file) {
  const buf = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
  let out = "";
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const tc = await page.getTextContent();
    out += tc.items.map((it) => it.str).join(" ") + "\n\n";
  }
  return out.trim();
}

// === OWNER DOKUMENTY — správa firemních dokumentů ===
// - Upload nového .md (drag & drop nebo paste) s metadaty
// - Edit existujícího (nová verze nebo minor edit)
// - Overview kdo co přečetl (per dokument)
// - Soft-delete
const KATEGORIE_OPTIONS = ["Onboarding", "Procesy", "Compliance", "Marketing", "Legal", "Ostatní"];
const ROLE_OPTIONS = [
  { value: "trener", label: "Trenéři" },
  { value: "sales", label: "Sales" },
  { value: "csm", label: "CSM" },
  { value: "owner", label: "Owner" },
  { value: "vsichni", label: "Všichni přihlášení" },
];

export default function Dokumenty() {
  const { data: ovData, loading, error, refetch, refreshing } = useApiData("/api/dokumenty?action=overview");
  const overview = ovData?.overview || [];

  const [editing, setEditing] = useState(null); // null nebo doc objekt (nový = {nazev:""})
  const [previewSlug, setPreviewSlug] = useState(null);

  return (
    <div className="space-y-4">
      <GradientHeader
        title="Dokumenty"
        subtitle="Firemní procesy, onboarding, compliance. Sleduj, kdo co přečetl."
        icon={BookOpen}
        loading={refreshing}
        akce={[
          { klic: "novy", ikona: Plus, popis: "Nový dokument", onClick: () => setEditing({ slug: "", nazev: "", popis: "", kategorie: "Procesy", body_md: "", povinne_role: "trener", deadline_dni: 14, isNew: true }) },
          { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: refreshing },
        ]}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">Chyba: {error}</div>
      )}
      {loading && (
        <div className="flex items-center justify-center py-10 text-sm text-slate-400">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Načítám…
        </div>
      )}

      {!loading && overview.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl py-10 px-4 text-center">
          <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <div className="text-sm text-slate-500">Zatím žádné dokumenty. Klikni „Nový dokument" výše.</div>
        </div>
      )}

      {/* Karty per dokument s overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {overview.map((d) => (
          <DocOverviewCard
            key={d.slug}
            doc={d}
            onEdit={() => setEditing({ ...d, isNew: false })}
            onPreview={() => setPreviewSlug(d.slug)}
            onDelete={async () => {
              if (!await confirmDialog(`Opravdu smazat dokument "${d.nazev}"? Sign-offs zůstanou v historii.`)) return;
              try {
                await fetchApi("/api/dokumenty", {
                  method: "POST",
                  body: JSON.stringify({ action: "delete", slug: d.slug }),
                });
                refetch();
              } catch (e) {
                toast("Smazání selhalo: " + e.message, "error");
              }
            }}
          />
        ))}
      </div>

      {/* Editor modal */}
      {editing && (
        <DokumentEditor
          doc={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refetch(); }}
        />
      )}

      {/* Preview modal */}
      {previewSlug && (
        <DokumentPreview slug={previewSlug} onClose={() => setPreviewSlug(null)} />
      )}
    </div>
  );
}

function DocOverviewCard({ doc, onEdit, onPreview, onDelete }) {
  const pct = doc.total > 0 ? Math.round((doc.signed / doc.total) * 100) : 0;
  const allDone = doc.total > 0 && doc.signed === doc.total;
  const noOne = doc.total === 0;
  const barColor = noOne ? "bg-slate-300" : allDone ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-orange-500";

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] text-slate-400 uppercase tracking-wider font-bold">{doc.kategorie}</div>
          <div className="text-base font-bold text-slate-900 leading-tight">{doc.nazev}</div>
          {doc.popis && <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{doc.popis}</div>}
        </div>
        <div className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold whitespace-nowrap">v{doc.version}</div>
      </div>

      {/* Progress bar */}
      <div className="mb-2">
        <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
          <span className="inline-flex items-center gap-1">
            <Users className="w-3 h-3" />
            {noOne ? "Žádný adresát" : `Přečetlo ${doc.signed} z ${doc.total}`}
          </span>
          {!noOne && <span className="font-semibold tabular-nums">{pct}%</span>}
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full ${barColor} transition-all`} style={{ width: `${noOne ? 0 : pct}%` }} />
        </div>
      </div>

      {/* Lidé per status */}
      {doc.people && doc.people.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-800 select-none">
            Detail kdo přečetl (klikni)
          </summary>
          <div className="mt-2 space-y-1 max-h-48 overflow-y-auto pr-1">
            {doc.people.map((p) => {
              const styles = {
                signed: { icon: CheckCircle2, color: "text-emerald-600", label: "přečteno" },
                outdated: { icon: AlertTriangle, color: "text-orange-600", label: `verze ${p.signedVersion}` },
                unread: { icon: AlertTriangle, color: "text-amber-600", label: "nečteno" },
              }[p.status] || { icon: AlertTriangle, color: "text-slate-400", label: p.status };
              const Icon = styles.icon;
              return (
                <div key={p.email} className="flex items-center justify-between gap-2 text-xs px-2 py-1 rounded hover:bg-slate-50">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-700 truncate">{p.linkedTrener || p.name || p.email}</div>
                    <div className="text-[11px] text-slate-400 truncate">{p.email}</div>
                  </div>
                  <div className={`inline-flex items-center gap-1 ${styles.color} font-semibold whitespace-nowrap`}>
                    <Icon className="w-3 h-3" />
                    {styles.label}
                  </div>
                </div>
              );
            })}
          </div>
        </details>
      )}

      {/* Akce */}
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
        <button onClick={onPreview} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 min-h-[44px] sm:min-h-0">
          <Eye className="w-3.5 h-3.5" /> Náhled
        </button>
        <button onClick={onEdit} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-brand-200 text-brand-700 hover:bg-brand-50 min-h-[44px] sm:min-h-0">
          <Edit3 className="w-3.5 h-3.5" /> Upravit
        </button>
        <button onClick={onDelete} className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 ml-auto min-h-[44px] sm:min-h-0">
          <Trash2 className="w-3.5 h-3.5" /> Smazat
        </button>
      </div>

      <div className="text-[11px] text-slate-400 mt-2 inline-flex items-center gap-1">
        <Calendar className="w-3 h-3" /> Aktualizováno {doc.updated_at}
      </div>
    </div>
  );
}

function DokumentEditor({ doc, onClose, onSaved }) {
  const user = getCurrentUser();
  const [form, setForm] = useState({
    slug: doc.slug || "",
    nazev: doc.nazev || "",
    popis: doc.popis || "",
    kategorie: doc.kategorie || "Procesy",
    body_md: doc.body_md || "",
    povinne_role: doc.povinne_role || "trener",
    deadline_dni: doc.deadline_dni || 14,
  });
  const [minor, setMinor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);
  const isNew = !!doc.isNew;

  // Pokud nový dokument, odvodíme slug z názvu
  useEffect(() => {
    if (!isNew) return;
    const slug = String(form.nazev)
      .toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .substring(0, 60);
    setForm((f) => ({ ...f, slug }));
  }, [form.nazev, isNew]);

  // Načíst body_md pokud edit existujícího (overview neobsahuje body)
  useEffect(() => {
    if (isNew || !doc.slug || form.body_md) return;
    fetchApi(`/api/dokumenty?slug=${encodeURIComponent(doc.slug)}`)
      .then((r) => {
        if (r.doc?.body_md) setForm((f) => ({ ...f, body_md: r.doc.body_md }));
      }).catch(() => {});
  }, [isNew, doc.slug, form.body_md]);

  const handleFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    setForm((f) => ({ ...f, body_md: text, nazev: f.nazev || file.name.replace(/\.md$/i, "") }));
  };

  const pdfRef = useRef(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfNote, setPdfNote] = useState("");
  const handlePdf = async (file) => {
    if (!file) return;
    setPdfBusy(true); setPdfNote(""); setErr("");
    try {
      const text = await extractPdfText(file);
      if (!text) throw new Error("PDF nemá textovou vrstvu (asi sken). Použij PDF s textem.");
      // Dlouhé dokumenty po částech (~5000 znaků na hranicích odstavců) — každá část
      // se zpracuje samostatným voláním pod 26s limitem Netlify funkce.
      const chunks = [];
      let buf = "";
      for (const para of text.split(/\n{2,}/)) {
        if (buf && (buf.length + para.length + 2) > 5000) { chunks.push(buf); buf = para; }
        else buf = buf ? buf + "\n\n" + para : para;
      }
      if (buf) chunks.push(buf);

      const parts = [];
      let aiOk = true;
      for (let i = 0; i < chunks.length; i++) {
        setPdfNote(`Zpracovávám přes AI… část ${i + 1} z ${chunks.length}`);
        const j = await fetchApi("/api/dokumenty", {
          method: "POST",
          body: JSON.stringify({ action: "importPdf", text: chunks[i], part: i + 1, total: chunks.length }),
        });
        parts.push((j.body_md || "").trim());
        if (!j.ai) aiOk = false;
      }
      setForm((f) => ({ ...f, body_md: parts.join("\n\n"), nazev: f.nazev || file.name.replace(/\.pdf$/i, "") }));
      setPdfNote(aiOk
        ? `PDF zpracováno přes AI do přehledného Markdownu (${chunks.length} ${chunks.length === 1 ? "část" : "části"}). Zkontroluj a ulož.`
        : "Importováno, ale část bez AI (viz obsah), zkontroluj formátování.");
    } catch (e) {
      setErr("Import PDF: " + (e?.message || e));
    } finally {
      setPdfBusy(false);
    }
  };

  const handleSave = async () => {
    setErr("");
    if (!form.nazev.trim()) { setErr("Vyplň název."); return; }
    if (!form.slug.trim()) { setErr("Slug nesmí být prázdný."); return; }
    setSaving(true);
    try {
      await fetchApi("/api/dokumenty", {
        method: "POST",
        body: JSON.stringify({
          action: "upload",
          minor,
          doc: {
            ...form,
            created_by: user?.email || "owner",
          },
        }),
      });
      onSaved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const charCount = form.body_md.length;
  const charLimit = 49000;
  const charPct = Math.round((charCount / charLimit) * 100);

  return (
    <div className="fixed inset-0 z-50 bg-brand-dark/50 backdrop-blur-sm flex items-start justify-center p-2 sm:p-4 overflow-y-auto pb-[max(1rem,env(safe-area-inset-bottom))]">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl my-4 sm:my-8">
        <div className="flex items-center justify-between gap-3 p-4 sm:p-5 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900 min-w-0">{isNew ? "Nový dokument" : `Upravit: ${doc.nazev}`}</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-xl shrink-0 min-h-[44px] sm:min-h-0">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-4">
          {/* Metadata */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-slate-700 mb-1 block">Název *</label>
              <input
                type="text"
                value={form.nazev}
                onChange={(e) => setForm((f) => ({ ...f, nazev: e.target.value }))}
                className="field w-full"
                placeholder="např. Onboarding nového trenéra"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 mb-1 block">Slug (URL) {isNew && <span className="text-slate-400 font-normal">(odvozeno z názvu)</span>}</label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                disabled={!isNew}
                className="field w-full font-mono disabled:bg-slate-50 disabled:text-slate-500"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-bold text-slate-700 mb-1 block">Krátký popis</label>
              <input
                type="text"
                value={form.popis}
                onChange={(e) => setForm((f) => ({ ...f, popis: e.target.value }))}
                className="field w-full"
                placeholder="Jednovětý souhrn co dokument obsahuje"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 mb-1 block">Kategorie</label>
              <select
                value={form.kategorie}
                onChange={(e) => setForm((f) => ({ ...f, kategorie: e.target.value }))}
                className="field w-full"
              >
                {KATEGORIE_OPTIONS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-700 mb-1 block">Deadline (dní od uploadu)</label>
              <input
                type="number"
                min="1" max="90"
                value={form.deadline_dni}
                onChange={(e) => setForm((f) => ({ ...f, deadline_dni: parseInt(e.target.value, 10) || 14 }))}
                className="field w-full"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-bold text-slate-700 mb-1 block">Kdo musí přečíst (čárkou oddělené role)</label>
              <div className="flex flex-wrap gap-1.5">
                {ROLE_OPTIONS.map((r) => {
                  const roles = String(form.povinne_role || "").split(",").map((s) => s.trim());
                  const active = roles.includes(r.value);
                  return (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => {
                        const newRoles = active ? roles.filter((x) => x !== r.value) : [...roles.filter(Boolean), r.value];
                        setForm((f) => ({ ...f, povinne_role: newRoles.join(",") }));
                      }}
                      className={`text-xs px-3 py-1.5 rounded-full border ${active ? "bg-brand-600 border-brand-600 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Body editor */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-bold text-slate-700">Markdown obsah</label>
              <div className="flex items-center gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".md,.markdown,text/markdown,text/plain"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                />
                <button
                  onClick={() => fileRef.current?.click()}
                  className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-slate-200 hover:bg-slate-50"
                >
                  <Upload className="w-3.5 h-3.5" /> Nahrát .md
                </button>
                <input
                  ref={pdfRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  className="hidden"
                  onChange={(e) => { handlePdf(e.target.files?.[0]); e.target.value = ""; }}
                />
                <button
                  onClick={() => pdfRef.current?.click()}
                  disabled={pdfBusy}
                  className="btn btn-primary"
                >
                  {pdfBusy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                  {pdfBusy ? "Zpracovávám…" : "Importovat PDF (AI)"}
                </button>
                <span className={`text-xs tabular-nums ${charPct > 90 ? "text-red-600 font-bold" : "text-slate-400"}`}>
                  {charCount.toLocaleString("cs-CZ")} / {charLimit.toLocaleString("cs-CZ")}
                </span>
              </div>
            </div>
            {pdfNote && (
              <div className="mb-2 text-xs text-brand-800 bg-brand-50 border border-brand-200 rounded-lg px-3 py-2">{pdfNote}</div>
            )}
            <textarea
              value={form.body_md}
              onChange={(e) => setForm((f) => ({ ...f, body_md: e.target.value }))}
              className="field w-full font-mono min-h-[400px]"
              placeholder="# Nadpis&#10;&#10;Markdown text…&#10;&#10;## Sekce&#10;- bod 1&#10;- bod 2&#10;&#10;**tučně** *kurzíva* `kód`"
            />
          </div>

          {/* Minor edit checkbox (jen u edit) */}
          {!isNew && (
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={minor}
                onChange={(e) => setMinor(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              Drobná oprava (typo, formulace), nezvyšovat verzi, sign-offs zůstanou platné
            </label>
          )}

          {err && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-sm text-red-700">{err}</div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 sm:p-5 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Zrušit</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary"
          >
            {saving ? <><RefreshCw className="w-4 h-4 animate-spin" /> Ukládám…</> : <><CheckCircle2 className="w-4 h-4" /> {isNew ? "Vytvořit a rozeslat úkoly" : (minor ? "Uložit (bez nové verze)" : "Uložit jako novou verzi")}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function DokumentPreview({ slug, onClose }) {
  const { data, loading } = useApiData(`/api/dokumenty?slug=${encodeURIComponent(slug)}`);
  const doc = data?.doc;

  return (
    <div className="fixed inset-0 z-50 bg-brand-dark/50 backdrop-blur-sm flex items-start justify-center p-2 sm:p-4 overflow-y-auto pb-[max(1rem,env(safe-area-inset-bottom))]">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-4 sm:my-8">
        <div className="flex items-center justify-between gap-3 p-4 sm:p-5 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">Náhled: {doc?.nazev || slug}</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-xl min-h-[44px] sm:min-h-0">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="p-4 sm:p-7">
          {loading && <div className="text-sm text-slate-400 text-center py-6"><RefreshCw className="w-4 h-4 animate-spin inline mr-2" />Načítám…</div>}
          {doc && (
            <article className="prose prose-slate prose-sm sm:prose-base max-w-none
              prose-headings:font-bold prose-headings:text-slate-900
              prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg
              prose-a:text-brand-700 prose-a:underline-offset-2
              prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-rose-700 prose-code:font-medium prose-code:before:content-none prose-code:after:content-none
              prose-pre:bg-slate-900 prose-pre:text-slate-100
              prose-blockquote:border-l-brand-400 prose-blockquote:text-slate-700
              prose-th:bg-slate-100 prose-th:font-semibold">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{doc.body_md || "*Tento dokument zatím nemá obsah.*"}</ReactMarkdown>
            </article>
          )}
        </div>
      </div>
    </div>
  );
}
